import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Descope } from '@descope/react-sdk';

import { Loader } from '@/components/loader/loader';
import { useDescopeFlow } from '@/hooks/use-descope-flow';
import { useNavigateTo } from '@/routes/hooks/use-navigate-to';
import { notify } from '@/ui/snackbar/notify';
import { WithHtmlSavvyTitle } from '@/with-html-savvy-title';
import { captureDescopeFlowError } from '@/auth/helpers/capture-descope-flow-error';
import { createDescopeErrorMessageTransformer } from '@/auth/helpers/descope-error-message-transformer';
import { isInvalidInvitationTokenError } from '@/auth/helpers/is-invalid-invitation-token-error';
import { useInvitedUserDetails } from '@/auth/hooks/use-invited-user-details.query';
import { useSignUpParams } from '@/auth/hooks/use-sign-up-params';
import { useSignUp } from '@/auth/hooks/use-sign-up.mutation';
import type {
  DescopeError,
  DescopeFlowErrorEvent,
  SignUpFlowSuccessEvent,
} from '@/auth/types';

interface NewUserFormData {
  email: string;
  accountName: string;
}

export const SignUpPage = () => {
  const { t } = useTranslation('auth');
  const { isFlowLoading, onReady } = useDescopeFlow();
  const navigateTo = useNavigateTo();
  const { invitedUserParams } = useSignUpParams();
  const { handleSignUp } = useSignUp();
  const [newUserFormData, setNewUserFormData] = useState<NewUserFormData>({
    email: '',
    accountName: '',
  });
  const { isLoading: isUserDetailsLoading } = useInvitedUserDetails({
    onSuccessHandler: invitedUser => {
      if (invitedUser?.account && invitedUserParams) {
        setNewUserFormData({
          accountName: invitedUser.account.name,
          email: invitedUserParams.email,
        });
      }
    },
  });

  const errorTransformer = useMemo(
    () => createDescopeErrorMessageTransformer(t),
    [t],
  );

  const handleSuccessfulSignUp = (event: SignUpFlowSuccessEvent) => {
    handleSignUp({
      descopeToken: event.detail.sessionJwt,
      firstName: event.detail.user?.givenName ?? '',
      lastName: event.detail.user?.familyName ?? '',
      title: event.detail.user?.middleName ?? '',
      ...(invitedUserParams?.spotlightLinkId
        ? { spotlightLinkId: invitedUserParams.spotlightLinkId }
        : {}),
    });
  };

  if (isUserDetailsLoading) {
    return <Loader />;
  }

  const handleError = (
    event: string | Event | DescopeFlowErrorEvent | DescopeError,
  ) => {
    if (isInvalidInvitationTokenError(event)) {
      return navigateTo.signUpInvitationExpired();
    }
    captureDescopeFlowError('sign-up', event);
    notify({ message: t`loginError` });
  };

  return (
    <WithHtmlSavvyTitle title={t`signUpPageTitle`}>
      {isFlowLoading && <Loader />}
      <Descope
        flowId={window.runTimeEnvs.VITE_DESCOPE_SIGN_UP_FLOW}
        onSuccess={handleSuccessfulSignUp}
        onError={handleError}
        errorTransformer={errorTransformer}
        onReady={onReady}
        form={{
          accountName: newUserFormData.accountName,
          email: newUserFormData.email,
          invitationEmail: newUserFormData.email,
        }}
      />
    </WithHtmlSavvyTitle>
  );
};
